import { useEffect, useRef, useState } from 'react';
import type { RegionKey, StatusPayload } from '@artifax/shared';
import { REGION_FULL_NAMES } from '@artifax/shared';
import { fetchStatus } from './api.js';
import { AGORA_NOTE, SERVICE_DESCRIPTIONS } from './constants.js';
import { detectRegion } from './detect.js';
import { updatedAgo } from './format.js';
import { LatencyChart } from './components/LatencyChart.js';
import { OverallBanner } from './components/OverallBanner.js';
import { RegionTabs } from './components/RegionTabs.js';
import { ServiceRow } from './components/ServiceRow.js';
import { StatusKey } from './components/StatusKey.js';

const REFRESH_MS = 60_000;
const TICK_MS = 30_000;
// If we haven't successfully refreshed in this long, treat the page as offline.
const LOST_AFTER_MS = 150_000;

export function App() {
  const [data, setData] = useState<StatusPayload | null>(null);
  const [errored, setErrored] = useState(false);
  const [region, setRegion] = useState<RegionKey>('US');
  const [now, setNow] = useState(() => Date.now());
  const lastOkAt = useRef(0);
  const detected = useRef<RegionKey>('US');

  useEffect(() => {
    const r = detectRegion();
    detected.current = r;
    setRegion(r);
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        const payload = await fetchStatus(controller.signal);
        if (!active) return;
        setData(payload);
        setErrored(false);
        lastOkAt.current = Date.now();
        setNow(Date.now());
      } catch {
        if (active) setErrored(true);
      }
    };
    void load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      controller.abort();
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Connection lost when monitoring is stale or we can't refresh.
  const connectivity: 'live' | 'lost' =
    !data || data.stale || now - lastOkAt.current > LOST_AFTER_MS ? 'lost' : 'live';
  const updatedText = data ? updatedAgo(data.updatedAt, now) : null;

  return (
    <div className="page">
      <main className="main">
        {!data && !errored ? (
          <>
            <div className="skeleton" />
            <div className="skeleton" />
          </>
        ) : (
          <OverallBanner connectivity={connectivity} overall={data?.overall ?? null} updatedText={updatedText} />
        )}

        {data && (
          <>
            <StatusKey />

            <RegionTabs region={region} detected={detected.current} onChange={setRegion} />

            <section className="card" aria-labelledby="regional-h">
              <div className="card__head">
                <h2 id="regional-h">Regional services · {REGION_FULL_NAMES[region]} region</h2>
                <span>90-day uptime</span>
              </div>
              <ServiceRow
                name="ArtifaxEvent"
                desc={SERVICE_DESCRIPTIONS.event}
                service={data.regions[region].event}
              />
            </section>

            <section className="card" aria-labelledby="global-h">
              <div className="card__head">
                <h2 id="global-h">Global services</h2>
                <span>90-day uptime</span>
              </div>
              <ServiceRow name="ArtifaxAgora" desc={SERVICE_DESCRIPTIONS.agora} service={data.global.agora} />
              <p className="card__note">{AGORA_NOTE}</p>
            </section>

            <LatencyChart
              eventLatency={data.regions[region].event.latencyMs}
              agoraLatency={data.global.agora.latencyMs}
            />
          </>
        )}
      </main>
    </div>
  );
}
